import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import SourceCard from "./SourceCard";

/**
 * SourceCards — wrapping row of source cards, shown above/before
 * the streaming answer text. isPreview=true renders the early sources_preview
 * payload (title + score only); isPreview=false renders the enriched payload
 * (adds "Why Selected" + full citation) once tse:sources_enriched lands.
 */
// Cards shown on an ordinary retrieval turn, and on a cause sweep — where the
// row has to hold every runbook the answer cites, so the ceiling is higher and
// the row wraps rather than hiding evidence the P-list points at.
const DEFAULT_VISIBLE = 5;
// The grid is auto-fill at ~3 per row, so this is three rows of cards above the
// answer in the worst case. Higher than that and the ranking the engineer asked
// for starts below the fold; lower and a seven-runbook sweep loses a card its
// own P-lines point at.
const MAX_VISIBLE = 8;

const SourceCards = ({ sources = [], alternatives = [], isPreview = false, activeNumber, citedNumbers, canReplace = false, replacingNumber = null, sourceAction = null, onCardClick, onOpenSourceMap, onReplaceSource, onCiteSource, onFlagSource }) => {
  // Open by default — the cards are the citation surface, so hiding them by
  // default would bury the evidence the answer rests on. Collapsing is for
  // reclaiming height on a long scrollback, per message.
  const [collapsed, setCollapsed] = useState(false);
  const [replacePickerNumber, setReplacePickerNumber] = useState(null);

  if (!sources.length) return null;
  // Only documents making their FIRST appearance in the session get a card. A
  // diagnosis runs many turns off one runbook, and redrawing its card every turn
  // buried the answer. Later turns cite it through the inline [n] pills instead,
  // which stay stable because the server numbers sources per session.
  const fresh = sources.filter((s) => s.is_new !== false);
  // `causes > 0` marks a document the answer's P-list was actually drawn from.
  // Those are never trimmed: a possible-causes answer names up to seven
  // runbooks, and a flat cut at five left P-lines pointing at documents with no
  // card while cards for documents contributing nothing sat above them. They
  // also lead the row — the server already orders them first, and this keeps
  // that order if it ever stops.
  const contributors = fresh.filter((s) => (s.causes || 0) > 0);
  const visible = contributors.length
    ? [...contributors, ...fresh.filter((s) => !(s.causes || 0))].slice(0, MAX_VISIBLE)
    : fresh.slice(0, DEFAULT_VISIBLE);
  if (!visible.length) return null;

  return (
    <div className="d5-source-cards-wrap">
      <button
        type="button"
        className="d5-source-cards-label"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((v) => !v)}
        title={collapsed ? "Show sources" : "Hide sources"}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
        {isPreview ? "Searching sources…" : `Sources (${visible.length})`}
      </button>
      {!collapsed && (
      <div className="d5-source-cards-row">
        {visible.map((src, i) => {
          const number = src.n || i + 1;
          // Only a card that names its document(s) can be scoped to a subgraph.
          // Without a handler there is nowhere to open it either.
          const canOpenMap = !!onOpenSourceMap && !!(src.file_names?.length || src.file_name);
          const isCited = !!citedNumbers?.has?.(number);
          const canEdit = canReplace && !isPreview;
          const busy = replacingNumber === number;
          return (
            <SourceCard
              key={src.id || src.nid || `${src.title}-${i}`}
              number={number}
              title={src.title}
              sourceType={src.sourceType || src.source_type}
              score={src.score}
              whySelected={src.whySelected || src.why_selected}
              citation={src.citation}
              url={src.url}
              isPreview={isPreview}
              isActive={activeNumber === number}
              isCited={isCited}
              isCausal={!!src.causal}
              canOpenMap={canOpenMap}
              canReplace={canEdit && alternatives.length > 0}
              canCite={canEdit && !isCited}
              replacing={busy && sourceAction !== "cite"}
              citing={busy && sourceAction === "cite"}
              actionsLocked={replacingNumber != null}
              pickerOpen={replacePickerNumber === number}
              alternatives={alternatives}
              onClick={() => onCardClick?.(src, number)}
              onOpenMap={() => onOpenSourceMap?.(src, number)}
              onToggleReplace={() => {
                setReplacePickerNumber((current) => current === number ? null : number);
              }}
              onReplace={(candidate) => {
                setReplacePickerNumber(null);
                onReplaceSource?.(src, number, candidate);
              }}
              onCite={() => {
                setReplacePickerNumber(null);
                onCiteSource?.(src, number);
              }}
              onFlag={onFlagSource ? () => onFlagSource(src, number) : undefined}
            />
          );
        })}
      </div>
      )}
    </div>
  );
};

export default SourceCards;
